(function () {
  'use strict';

  const RULES = Object.freeze({
    piste: {
      beginner: { nimble: [-15, -10], stable: [-12, -7] },
      intermediate: { nimble: [-12, -7], stable: [-10, -5] },
      advanced: { nimble: [-10, -5], stable: [-5, 0] }
    },
    'all-mountain': {
      beginner: { nimble: [-10, -5], stable: [-7, -2] },
      intermediate: { nimble: [-5, 0], stable: [0, 5] },
      advanced: { nimble: [-5, 0], stable: [0, 5] }
    },
    park: {
      beginner: { nimble: [-10, -5], stable: [-7, -2] },
      intermediate: { nimble: [-10, -5], stable: [-5, 0] },
      advanced: { nimble: [-5, 0], stable: [0, 5] }
    },
    freeride: {
      beginner: { nimble: [-5, 0], stable: [0, 5] },
      intermediate: { nimble: [-5, 0], stable: [0, 5] },
      advanced: { nimble: [0, 5], stable: [5, 10] }
    }
  });

  const CATEGORY_META = Object.freeze({
    piste: {
      label: 'Pistskidor',
      url: '/collections/pistskidor',
      explanation: {
        nimble: 'En kortare pistskida passar korta, snabba svängar och är lättare att få in i svängen.',
        stable: 'En längre pistskida i spannet ger mer stöd i större svängar och högre fart.'
      }
    },
    'all-mountain': {
      label: 'All mountain-skidor',
      url: '/collections/all-mountain-skidor',
      explanation: {
        nimble: 'Den kortare delen av spannet prioriterar kvickhet i pist, pucklar och tajtare terräng.',
        stable: 'Kroppslängd eller något längre ger mer stabilitet och lugn i uppåkt och varierande snö.'
      }
    },
    park: {
      label: 'Twintip- och parkskidor',
      url: '/collections/twintipskidor',
      explanation: {
        nimble: 'Kortare längd ger lägre svingvikt och enklare manövrering i rotationer och på rails.',
        stable: 'Mer längd ger en större plattform i landningar och bättre stabilitet över hela berget.'
      }
    },
    freeride: {
      label: 'Freeride- och offpistskidor',
      url: '/collections/offpistskidor',
      explanation: {
        nimble: 'Kroppslängd eller strax under ger en mer lätthanterlig känsla i skog och teknisk terräng.',
        stable: 'En längre skida ger mer flyt, fartstabilitet och dämpning i mjuk eller uppåkt snö.'
      }
    }
  });

  const ADULT_LENGTH_RANGE = Object.freeze({ min: 152, max: 193 });
  const ADULT_UPPER_STOCK_RANGE = Object.freeze({ min: 187, max: 193 });

  function clampAdultLength(length) {
    return Math.min(ADULT_LENGTH_RANGE.max, Math.max(ADULT_LENGTH_RANGE.min, length));
  }

  function calculateRecommendation(input) {
    const height = Number(input.height);
    const level = input.level;
    const category = input.category;
    const feel = input.feel;

    if (!Number.isFinite(height) || height < 130 || height > 215) {
      throw new RangeError('Kroppslängden måste vara mellan 130 och 215 cm.');
    }

    const band = RULES[category] && RULES[category][level] && RULES[category][level][feel];
    if (!band) {
      throw new TypeError('Välj åknivå, huvudsaklig åkning och önskad känsla.');
    }

    const meta = CATEGORY_META[category];
    const rawMin = Math.round(height + band[0]);
    const rawMax = Math.round(height + band[1]);
    const reachesUpperStockRange = rawMax >= ADULT_LENGTH_RANGE.max;
    const min = reachesUpperStockRange ? ADULT_UPPER_STOCK_RANGE.min : clampAdultLength(rawMin);
    const max = reachesUpperStockRange ? ADULT_UPPER_STOCK_RANGE.max : clampAdultLength(rawMax);
    const caveats = [];

    if (level === 'beginner') {
      caveats.push('Som nybörjare är en mjukare och mer lättsvängd konstruktion minst lika viktig som längden.');
    } else if (level === 'advanced') {
      caveats.push('Som avancerad åkare kan du använda den övre delen för mer fartstabilitet eller den nedre för mer kvickhet.');
    }

    if (category === 'piste') {
      caveats.push('SL-inriktade skidor väljs normalt kortare än GS-inriktade modeller.');
    } else {
      caveats.push('Mycket rocker förkortar den effektiva kanten och kan göra att en längre skida fortfarande känns lätthanterlig.');
    }

    if (category === 'freeride' && level === 'beginner') {
      caveats.push('Freeride ställer högre krav på teknik och omdöme; ta gärna hjälp av en skidexpert innan köp.');
    }

    caveats.push('Lättare åkare kan börja i nedre delen och tyngre eller kraftfullare åkare i den övre.');

    if (rawMin < ADULT_LENGTH_RANGE.min) {
      caveats.push('Intervallet är anpassat till vuxenmodellernas vanligaste tillgängliga längder, från cirka 152 cm. Kontakta oss om du behöver en kortare modell.');
    } else if (reachesUpperStockRange) {
      caveats.push('De längsta tillgängliga vuxenmodellerna varierar vanligtvis mellan 187 och 193 cm. Rätt längd inom spannet beror på skidmodell, konstruktion och åkstil.');
    }

    return {
      min: min,
      max: max,
      category: category,
      label: meta.label,
      url: meta.url,
      explanation: meta.explanation[feel],
      caveat: caveats.join(' ')
    };
  }

  function normalizeText(value) {
    return String(value || '')
      .toLocaleLowerCase('sv-SE')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function pushDataLayer(payload) {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function productHandleFromUrl(url) {
    const match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { calculateRecommendation, RULES, CATEGORY_META, ADULT_LENGTH_RANGE, ADULT_UPPER_STOCK_RANGE };
  }

  if (typeof window === 'undefined' || typeof document === 'undefined' || !window.customElements) return;

  class SkiLengthGuide extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.form = this.querySelector('[data-ski-guide-form]');
      this.result = this.querySelector('[data-ski-guide-result]');
      this.error = this.querySelector('[data-ski-guide-error]');
      this.articleBody = this.querySelector('[data-ski-guide-article-body]');
      this.started = false;

      this.form?.addEventListener('input', () => this.trackStart());
      this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
      this.addEventListener('click', (event) => this.handleTrackedClick(event));

      this.splitOpeningAdvice();
      this.placeContextualPaths();
      this.placeExpertHelp();
    }

    trackStart() {
      if (this.started) return;
      this.started = true;
      pushDataLayer({
        event: 'ski_guide_start',
        module: 'length-finder',
        article_handle: this.dataset.articleHandle || ''
      });
    }

    handleSubmit(event) {
      event.preventDefault();
      this.trackStart();

      const formData = new FormData(this.form);
      const input = {
        height: formData.get('height'),
        level: formData.get('level'),
        category: formData.get('category'),
        feel: formData.get('feel')
      };

      try {
        const recommendation = calculateRecommendation(input);
        this.showRecommendation(recommendation, input);
      } catch (error) {
        this.showError(error.message);
      }
    }

    showRecommendation(recommendation, input) {
      this.error.hidden = true;
      this.error.textContent = '';

      const rangeLabel = recommendation.min === recommendation.max
        ? `${recommendation.min} cm`
        : `${recommendation.min}–${recommendation.max} cm`;

      this.querySelector('[data-ski-guide-range]').textContent = rangeLabel;
      this.querySelector('[data-ski-guide-category-label]').textContent = recommendation.label;
      this.querySelector('[data-ski-guide-explanation]').textContent = recommendation.explanation;
      this.querySelector('[data-ski-guide-caveat]').textContent = recommendation.caveat;

      const resultLink = this.querySelector('[data-ski-guide-result-link]');
      resultLink.href = recommendation.url;
      resultLink.dataset.skiGuideCategory = recommendation.category;

      this.result.hidden = false;
      this.result.focus?.({ preventScroll: true });
      this.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      pushDataLayer({
        event: 'ski_guide_recommendation',
        article_handle: this.dataset.articleHandle || '',
        height_cm: Number(input.height),
        skill_level: input.level,
        ski_category: input.category,
        feel: input.feel,
        min_cm: recommendation.min,
        max_cm: recommendation.max
      });
    }

    showError(message) {
      this.result.hidden = true;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    handleTrackedClick(event) {
      const tracked = event.target.closest('[data-ski-guide-track]');
      if (tracked && this.contains(tracked)) {
        const eventType = tracked.dataset.skiGuideTrack;
        const common = {
          article_handle: this.dataset.articleHandle || '',
          module: tracked.dataset.skiGuideModule || '',
          destination_url: tracked.href || ''
        };

        if (eventType === 'collection') {
          pushDataLayer({
            event: 'ski_guide_collection_click',
            ...common,
            ski_category: tracked.dataset.skiGuideCategory || ''
          });
        } else if (eventType === 'expert') {
          pushDataLayer({
            event: 'ski_guide_expert_click',
            ...common,
            contact_method: tracked.dataset.skiGuideContactMethod || ''
          });
        }
        return;
      }

      const productLink = event.target.closest('[data-guide-product-path] [data-grid-link][href*="/products/"]');
      if (!productLink || !this.contains(productLink)) return;

      const path = productLink.closest('[data-guide-product-path]');
      const productCard = productLink.closest('[data-product-id]');
      pushDataLayer({
        event: 'ski_guide_product_click',
        article_handle: this.dataset.articleHandle || '',
        module: 'article-product-path',
        ski_category: path?.dataset.guideProductPath || '',
        product_id: productCard?.dataset.productId || '',
        product_handle: productHandleFromUrl(productLink.href),
        destination_url: productLink.href || ''
      });
    }

    splitOpeningAdvice() {
      if (!this.articleBody) return;

      const heading = [...this.articleBody.querySelectorAll('h2')].find((item) =>
        normalizeText(item.textContent).includes('hur långa skidor ska jag ha')
      );
      if (!heading) return;

      let list = heading.nextElementSibling;
      while (list && list.tagName !== 'UL' && list.tagName !== 'OL') list = list.nextElementSibling;
      if (!list) return;

      const items = [...list.children];
      const markerIndex = items.findIndex((item) =>
        normalizeText(item.textContent).startsWith('skidan bör vara i din längd eller lite över')
      );
      if (markerIndex < 0) return;

      const shortHeading = document.createElement('h3');
      shortHeading.className = 'ski-guide__split-heading';
      shortHeading.textContent = 'Välj en kortare skida när';
      list.parentNode.insertBefore(shortHeading, list);

      const longHeading = document.createElement('h3');
      longHeading.className = 'ski-guide__split-heading';
      longHeading.textContent = 'Välj kroppslängd eller längre när';

      const secondList = list.cloneNode(false);
      items[markerIndex].remove();
      items.slice(markerIndex + 1).forEach((item) => secondList.appendChild(item));
      list.after(longHeading, secondList);
    }

    placeContextualPaths() {
      if (!this.articleBody) return;

      const headings = [...this.articleBody.querySelectorAll('h2')];
      const matchers = {
        'all-mountain': (text) => text.includes('all mountain'),
        park: (text) => text.includes('twintip'),
        freeride: (text) => text.includes('freeride') || text.includes('offpist'),
        piste: (text) => text === 'pistskidor' || text.startsWith('pistskidor ')
      };

      Object.entries(matchers).forEach(([category, matcher]) => {
        const path = this.querySelector(`[data-guide-move-after="${category}"]`);
        if (!path) return;

        const headingIndex = headings.findIndex((heading) => matcher(normalizeText(heading.textContent)));
        if (headingIndex < 0) return;

        const nextHeading = headings[headingIndex + 1];
        if (nextHeading) {
          this.articleBody.insertBefore(path, nextHeading);
        } else {
          this.articleBody.appendChild(path);
        }
      });
    }

    placeExpertHelp() {
      if (!this.articleBody) return;
      const expert = this.querySelector('[data-ski-guide-expert]');
      const faqHeading = [...this.articleBody.querySelectorAll('h2')].find((heading) =>
        normalizeText(heading.textContent).includes('vanliga frågor')
      );
      if (expert && faqHeading) this.articleBody.insertBefore(expert, faqHeading);
    }
  }

  if (!window.customElements.get('ski-length-guide')) {
    window.customElements.define('ski-length-guide', SkiLengthGuide);
  }
})();
